import { NonEmptyArray, Option, ReadonlyArray, Runtime } from "@morphism/fp";
import { DatePicker } from "antd";
import * as Moment from "moment";
import React, { useMemo } from "react";
import { Redirect, useHistory, useRouteMatch } from "react-router-dom";
import { Company } from "~/Company";
import * as Data from "~/Data";
import { Distance } from "~/Distance";
import * as Driver from "~/Driver";
import { DVIRs } from "~/DVIR";
import { Graph } from "~/Graph";
import * as Icon from "~/Icon";
import * as Layout from "~/Layout";

import {
  NotApplicable as GenericNotApplicable,
  NotApplicable,
} from "~/NotApplicable";
import { Order } from "~/Order";
import { Radio } from "~/Radio";
import { Remote } from "~/Remote";
import { Request } from "~/Request";
import { Tag } from "~/Tag";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

import { Calendar as Calendar_ } from "./Calendar";
import { Download, ExportMenu } from "./ExportMenu";
import { useWorkReportingLocationFilters } from "~/Driver/Filter";
import { Page as Page_ } from "./Page";
import { Table as Table_ } from "./Table";
import { DisabledTable } from "~/DisabledTable";

export type Summaries = { readonly summaries: readonly Summary[] };
export function Summaries({
  missingDVIRs = false,
  baseRoute,
  driver,
}: Summaries.Props) {
  const route = Summaries.Route.use(baseRoute);

  const { interval } =
    Option.toNullable(route) ??
    pipe(Time.DateTime.toStartOfDay(), (now) =>
      !missingDVIRs
        ? Summaries.Interval.initial(now, driver)
        : DVIRs.Interval.initial()
    );

  const { toggle } = Summaries.Toggle.use(
    missingDVIRs ? { toggle: "table" } : undefined
  );

  const {
    isLoading,
    isLoadingSummaries,
    isLoadingDvirSummaries,
    isLoadingShifts,
    summaries,
    hasDvirSummaries,
    shiftErrors,
    dvirSummaryErrors,
  } = Summaries.use({ interval, driver, missingDVIRs });

  const summariesContainBrachInfo =
    summaries.summaries.filter((s) => !!s.summary.driver.branch?.branch_id)
      .length > 0;

  const [calendar, table] = React.useMemo(() => {
    return [
      !missingDVIRs && (
        <Summaries.Calendar
          {...{ baseRoute, isLoading, toggle, driver, interval, ...summaries }}
        />
      ),
      shiftErrors ? (
        Graph.Errors.renderApolloError(
          "Error while retrieving Shifts",
          shiftErrors
        )
      ) : (
        <Summaries.Table
          {...{
            missingDVIRs,
            isLoading,
            isLoadingSummaries,
            isLoadingDvirSummaries,
            isLoadingShifts,
            hasDvirSummaries,
            baseRoute,
            driver,
            interval,
            ...summaries,
          }}
        />
      ),
    ];
  }, [
    interval.start.dateTime,
    interval.stop.dateTime,
    isLoading,
    isLoadingDvirSummaries,
    isLoadingSummaries,
    summariesContainBrachInfo,
  ]);

  return (
    <Layout.Section.Component>
      {Option.isNone(route) && (
        <Redirect to={Summaries.Route.href({ baseRoute, interval })} />
      )}
      {React.useMemo(() => {
        return (
          <>
            <Summaries.Header
              {...{
                missingDVIRs,
                baseRoute,
                toggle,
                driver,
                interval,
                ...summaries,
              }}
            />
            {dvirSummaryErrors
              ? Graph.Errors.renderApolloError(
                  "Error while retrieving DVIR Summaries",
                  dvirSummaryErrors
                )
              : null}
            <div style={{ overflow: "scroll" }}>
              {toggle.when({ calendar: () => calendar, table: () => table })}
            </div>
          </>
        );
      }, [
        toggle.updated.dateTime.valueOf(),
        interval.start.dateTime,
        interval.stop.dateTime,
        isLoading,
        isLoadingDvirSummaries,
        isLoadingSummaries,
        summariesContainBrachInfo,
      ])}
    </Layout.Section.Component>
  );
}

export function PromptToAddFiltersForLogs({
  missingDVIRs = false,
  isLoading=false,
  driver=undefined,
  baseRoute,
}: Summaries.Props) {
  const route = Summaries.Route.use(baseRoute);

  const { interval } =
    Option.toNullable(route) ??
    pipe(Time.DateTime.toStartOfDay(), (now) =>
      !missingDVIRs
        ? Summaries.Interval.initial(now, driver)
        : DVIRs.Interval.initial()
    );

  const { toggle } = Summaries.Toggle.use(
    missingDVIRs ? { toggle: "table" } : undefined
  );

  const columns = Table_.useColumns({
    summaries: [],
    missingDVIRs,
    driver,
  })

  const disabledText = "Please select a branch or location filter to view logs"

  return (
    <Layout.Section.Component>
      {Option.isNone(route) && (
        <Redirect to={Summaries.Route.href({ baseRoute, interval })} />
      )}
      <Summaries.Header
        {...{
          missingDVIRs,
          baseRoute,
          toggle,
          interval,
          summaries: [],
        }}
      />

      <DisabledTable  isLoading={isLoading} disabledText={disabledText} columns={columns as readonly unknown[]} />

    </Layout.Section.Component>
  );
}

export namespace Summaries {
  export import Table = Table_;
  export import Calendar = Calendar_;
  export import Page = Page_;

  export type Props = {
    readonly missingDVIRs?: boolean;
    readonly isLoading?: boolean;
    readonly initialDuration?: Time.Duration;
    readonly baseRoute: string;
    readonly driver?: Driver.Driver;
  };

  export const isMissingDVIR = ({ summary }: Summary) => {
    return (
      summary?.shift &&
      summary?.shift.distance !== undefined &&
      summary?.shift.dvirSummaries &&
      summary?.shift.dvirSummaries.length < 1 &&
      summary?.shift.assets &&
      summary?.shift.assets.length > 0
    );
  };

  export type PropsWithData = Props & Time.Interval.Stopped & Summaries;
  export type Identity = Time.Interval.Stopped &
    Company & { readonly driver: Driver.Driver };

  export const empty = (): Summaries => ({ summaries: [] });
  export const concat = (a: Summaries, b: Summaries): Summaries => ({
    summaries: [...a.summaries, ...b.summaries],
  });

  export const use = ({
    interval,
    driver,
    missingDVIRs,
  }: {
    readonly interval: Time.Interval.Stopped["interval"];
    readonly driver?: Driver.Driver;
    readonly missingDVIRs: boolean;
  }) => {
    const [
      _wrlLoading,
      reportingLocationIDs,
    ] = useWorkReportingLocationFilters();

    const [
      _branchesLoading,
      branchIDs,
    ] = Company.Branch.Branches.useBranchFiltersFromPreferences();

    const {
      shifts,
      loading: shiftsLoading,
      isFetchingMore,
      errors: shiftErrors,
    } = Driver.Shifts.Query.use({
      interval,
      reportingLocationIDs,
      branchIDs,
      driver,
    });

    const {
      data: companyDvirSummary,
      loading: isLoadingCompanyDvirSummaries,
      errors: dvirSummaryErrors,
    } = DVIRs.Summary.use({
      interval: {
        start: interval.start,
        stop: Time.DateTime.toEndOfDay(interval.stop),
      },
      reportingLocationIDs,
      branchIDs,
      driverIDs: driver && driver.id ? [driver.id] : undefined,
    });

    const summariesUnfiltered = Summaries.Query.use({
      interval,
      shifts,
      companyDvirSummary: companyDvirSummary ?? [],
      driver,
      loadingShifts: isFetchingMore || shiftsLoading,
    });

    const isLoadingSummaries = Remote.isLoading(summariesUnfiltered);
    const isLoadingShifts = shiftsLoading || isFetchingMore;

    const isLoadingMissingDVIRs =
      missingDVIRs !== undefined &&
      (isLoadingShifts || isLoadingCompanyDvirSummaries);

    const summaries = !missingDVIRs
      ? pipe(summariesUnfiltered, Remote.getOrElse(Summaries.empty))
      : pipe(
          summariesUnfiltered,
          Remote.getOrElse(Summaries.empty),
          Summaries.filterAndSeparateMissingDVIRs
        );

    const hasDvirSummaries = pipe(
      summariesUnfiltered,
      Remote.getOrElse(Summaries.empty),
      (summaries) =>
        summaries?.summaries?.some(
          (s) => (s.summary.shift?.dvirSummaries ?? [])?.length > 0
        )
    );

    const isErrorShifts = shifts.error !== undefined;

    const isLoading =
      (isLoadingShifts && !isErrorShifts) ||
      isLoadingSummaries ||
      isLoadingMissingDVIRs ||
      isLoadingCompanyDvirSummaries;

    const isLoadingDvirSummaries = isLoadingCompanyDvirSummaries;

    const resolvedUnfiltered = useMemo(
      () =>
        Remote.getOrElse(() => ([] as unknown) as Summaries)(
          summariesUnfiltered
        ),
      [summariesUnfiltered.remote]
    );

    const shiftsWithDVIRs = useMemo(
      () =>
        resolvedUnfiltered.summaries?.reduce<number>((prev, current) => {
          if (current?.summary.shift && !isMissingDVIR(current))
            return prev + 1;
          return prev;
        }, 0) ?? 0,
      [resolvedUnfiltered]
    );

    const totalShifts = useMemo(
      () =>
        resolvedUnfiltered.summaries?.reduce<number>((prev, current) => {
          if (current?.summary.shift) return prev + 1;
          return prev;
        }, 0) ?? 0,
      [resolvedUnfiltered]
    );

    const shiftsWithoutDVIRs = totalShifts - shiftsWithDVIRs;

    const useResult = {
      isLoading,
      isLoadingSummaries,
      isLoadingDvirSummaries,
      isLoadingShifts,
      summaries,
      hasDvirSummaries,
      shiftErrors,
      dvirSummaryErrors,
      // If we're filtering to those logs with missing DVIRs, these will have
      // non-null values with their respective counts
      shiftsWithDVIRs: isLoading ? null : shiftsWithDVIRs,
      shiftsWithNoDVIRs: isLoading ? null : shiftsWithoutDVIRs,
    } as const;

    return useResult;
  };

  export const request = ({
    company,
    driver,
    interval,
    reportingLocationIDs,
    branchIDs,
  }: {
    // eslint-disable-next-line functional/prefer-readonly-type
    readonly reportingLocationIDs?: number[];
    readonly branchIDs?: Company.Branch.Branches.BranchIDs;
  } & Pick<Identity, "company" | "interval"> &
    Partial<Pick<Identity, "driver">>): Request.Fn<Summaries> => {
    const queryString = Driver.Request.buildQueryString({
      reportingLocationIDs,
      branchIDs,
    });

    const start = Time.DateTime.toISOWithoutTime(interval.start);
    const stop = Time.DateTime.toISOWithoutTime(interval.stop);

    const endpoint = driver
      ? `/companies/${company.id}/drivers/${driver.id}/logs/${start}/${stop}`
      : `/companies/${company.id}/logs/${start}/${stop}${queryString}`;

    return flow(
      Request.fromConfig({ endpoint }),
      Request.Expect.status(200),
      Request.Expect.json(Response.runtime()),
      Request.onSuccess(Response.decode)
    );
  };

  export const filterAndSeparateMissingDVIRs = ({
    summaries,
  }: Summaries): Summaries => ({
    summaries: summaries
      .filter((s) => isMissingDVIR(s))
      .reduce<readonly Summary[]>((p, c) => {
        if (c.summary?.shift?.assets && c.summary?.shift?.assets.length > 1) {
          const perAssetSummary = c.summary?.shift?.assets.map((asset) => ({
            summary: {
              ...c.summary,
              // Route per-vehicle distance through summary.distance so that
              // distanceMiles (which falls back here when shift.distance is
              // absent) returns the correct per-asset value — including zero —
              // without triggering the > 0 fallback to the batch total.
              distance:
                c.summary.shift?.distanceByAsset?.[asset.asset.id] ??
                { miles: 0 },
              shift: c.summary.shift
                ? {
                    ...c.summary.shift,
                    assets: [asset],
                    distance: undefined,
                  }
                : undefined,
            },
          }));
          return [...p, ...perAssetSummary];
        }
        return [...p, c];
      }, []),
  });

  export namespace Query {
    export const use = ({
      interval,
      shifts = {},
      companyDvirSummary = [],
      driver,
      loadingShifts,
    }: Time.Interval.Stopped & {
      readonly shifts?: Driver.Shifts;
      readonly companyDvirSummary?: readonly DVIRs.Summary[];
      readonly driver?: Driver.Driver;
      readonly loadingShifts?: boolean;
    }): Remote<Summaries> => {
      const drivers = Driver.useStore();
      const { company } = Company.use();
      const [
        wrlFiltersLoading,
        reportingLocationIDs,
      ] = useWorkReportingLocationFilters();

      const [
        branchesLoading,
        branchIDs,
      ] = Company.Branch.Branches.useBranchFiltersFromPreferences();

      const summariesWithoutShift = Remote.Query.use(
        [
          "Log Summaries",
          company.id,
          driver?.id,
          interval.start,
          interval.stop,
          reportingLocationIDs,
          branchIDs,
        ],
        Summaries.request({
          company,
          driver,
          interval,
          reportingLocationIDs: !wrlFiltersLoading
            ? reportingLocationIDs
            : undefined,
          branchIDs: !branchesLoading ? branchIDs : undefined,
        })
      );

      const companyDriverDvirSummaries = companyDvirSummary
        .map((driverWithDvirs) => driverWithDvirs.driversSummary)
        .reduce((previous, current) => [...previous, ...current], []);

      return React.useMemo(() => {
        return pipe(
          summariesWithoutShift,
          Remote.map(({ summaries }) => ({
            summaries: pipe(
              summaries,
              ReadonlyArray.map<Summary, Summary>(({ summary }) => {
                // eslint-disable-next-line functional/no-let
                let driver = undefined;
                if (
                  drivers.latest.remote !== "loading" &&
                  !summary.driver.homeOffice
                ) {
                  driver = pipe(
                    drivers,
                    Data.Store.Get.fromReference({
                      ...summary.driver,
                      missing: () => summary.driver,
                      found: (driver) => driver,
                    })
                  );
                }

                const shiftsForSummary = Option.toUndefined(
                  getShiftsForSummary(
                    shifts[summary.driver.id] ?? [],
                    companyDriverDvirSummaries,
                    { summary }
                  )
                );

                const updatedSummary: Summary = {
                  summary: {
                    ...summary,
                    shift: shiftsForSummary,
                    driver: driver ?? summary.driver,
                  },
                };

                return {
                  summary: {
                    ...updatedSummary.summary,
                    issues: {
                      ...updatedSummary.summary.issues,
                      missingDVIR: isMissingDVIR(updatedSummary),
                    },
                  },
                };
              })
            ),
          }))
        );
      }, [
        Remote.getOrElse(Summaries.empty)(summariesWithoutShift).summaries
          .length,
        companyDvirSummary.length,
        loadingShifts,
        reportingLocationIDs,
        drivers.latest.remote !== "loading",
      ]);
    };

    export const getShiftsForSummary = (
      driverShifts: readonly Driver.Shift[],
      companyDriverDvirSummaries: readonly DVIRs.Summary.DriverDvirSummary[],
      { summary }: Summary
    ): Option<
      Driver.Shift & {
        readonly dvirSummaries?: readonly DVIRs.Summary.DriverDvirSummary[];
        readonly distanceByAsset: Readonly<Record<string, Distance.Miles>>;
      }
    > => {
      const summaryDayInterval = Time.DateTime.toDayInterval(summary);
      const matches = driverShifts.filter((shift) => {
        if (Time.Interval.isStopped(shift)) {
          if (
            Time.Interval.Stopped.contains(shift.interval.start)(
              summaryDayInterval
            )
          ) {
            return true;
          }
        }

        return false;
      });

      if (matches.length === 0) {
        return Option.none;
      }

      const match = matches[0];

      // A single calendar day can contain multiple driving segments (e.g. after
      // a sleeper-berth restart), so sum mileage and merge assets across all
      // matching shifts. Assets are deduped by id to handle same-vehicle restarts
      // without duplication, while still capturing a vehicle swap mid-day.
      const totalMiles = matches.reduce(
        (acc, shift) => acc + (shift.distance?.miles ?? 0),
        0
      );

      const mergedAssets = [
        ...new Map(
          matches.flatMap((s) => s.assets).map((a) => [a.asset.id, a])
        ).values(),
      ];

      const distanceByAsset = matches.reduce<Record<string, Distance.Miles>>(
        (acc, shift) =>
          shift.assets.reduce(
            (a, asset) => ({
              ...a,
              [asset.asset.id]: {
                miles: (a[asset.asset.id]?.miles ?? 0) + (shift.distance?.miles ?? 0),
              },
            }),
            acc
          ),
        {}
      );

      const dvirSummaries = companyDriverDvirSummaries
        .filter(
          (driverWithDvirs) =>
            `${driverWithDvirs.driverID}` === summary.driver.id
        )
        .filter(
          (driverWithDvirs) =>
            Time.Interval.isStopped(match) &&
            (Time.Interval.Stopped.contains(driverWithDvirs.minDate)(summaryDayInterval) ||
              Time.Interval.Stopped.contains(driverWithDvirs.maxDate)(summaryDayInterval))
        );

      // Preserve shift.distance whenever any shift had hasDriveTime, even if
      // the summed miles happen to be 0 (e.g. GPS-rounded short moves). isMissingDVIR
      // uses distance !== undefined as a proxy for "driver drove", so omitting it on
      // a zero-but-drove day would silently drop them from the missing-DVIR table.
      const anyHadDriveTime = matches.some((s) => s.distance !== undefined);

      return Option.some({
        ...match,
        assets: mergedAssets,
        distanceByAsset,
        ...(anyHadDriveTime && { distance: { miles: totalMiles } }),
        dvirSummaries,
      });
    };
  }

  namespace Response {
    export const runtime = () => Runtime.array(Summary.Response.runtime());
    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Summaries => ({ summaries: response.map(Summary.Response.decode) });
  }

  export namespace Route {
    export const definition = (baseRoute: string) =>
      `${baseRoute}/:start/:stop?`;

    export const href = ({
      baseRoute,
      interval: { start, stop },
    }: Time.Interval.Stopped & { readonly baseRoute: string }) =>
      pipe(
        [start, stop],
        ReadonlyArray.map(Time.DateTime.toISOWithoutTime),
        ([start, stop]) => `${baseRoute}/${start}/${stop}`
      );

    export const use = (baseRoute: string): Option<Time.Interval.Stopped> =>
      pipe(
        useRouteMatch<{ readonly start?: string; readonly stop?: string }>(
          definition(baseRoute)
        ),
        Option.fromNullable,
        Option.chain(({ params: { start, stop } }) =>
          start && stop ? Option.some({ start, stop }) : Option.none
        ),
        Option.chain(({ start, stop }) =>
          Time.Interval.Stopped.fromDateStrings(start)(stop)
        )
      );
  }

  export function Interval({
    interval,
    ...props
  }: Pick<PropsWithData, "baseRoute" | "interval">) {
    const history = useHistory();
    return (
      <DatePicker.RangePicker
        allowClear={false}
        allowEmpty={[false, false]}
        format="MM/DD/YYYY"
        defaultValue={[
          Moment.default(interval.start.dateTime),
          Moment.default(interval.stop.dateTime),
        ]}
        onChange={flow(
          Option.fromNullable,
          Option.chain(([start, stop]) =>
            start && stop ? Option.some({ start, stop }) : Option.none
          ),
          Option.map(({ start, stop }) =>
            pipe(
              Time.Interval.Stopped.fromDateTimes(
                Time.DateTime.fromNumber(start.valueOf()),
                Time.DateTime.fromNumber(stop.valueOf())
              ),
              (interval) => history.push(Route.href({ ...props, ...interval }))
            )
          )
        )}
      />
    );
  }

  export namespace Interval {
    export const defaultDaySpan = 1 as const;
    export const defaultDaysSpanForSingleDriver = 30 as const;

    export const initial = (
      now: Time.DateTime,
      driver?: Driver.Driver,
      defaultDays?: number
    ): Time.Interval.Stopped => {
      const days = Time.Duration.days(
        defaultDays
          ? defaultDays
          : driver
          ? defaultDaysSpanForSingleDriver
          : defaultDaySpan
      );
      const dateTime = now.dateTime - days.duration;
      return { interval: { start: { dateTime }, stop: now } };
    };
  }

  export type Toggle = { readonly toggle: "calendar" | "table" };
  export namespace Toggle {
    export type Props = ReturnType<typeof use>;
    export const use = (initial?: Toggle) => {
      const [{ toggle, updated }, updateToggle] = React.useState({
        updated: Time.DateTime.now(),
        toggle: Table.Settings.isEmpty(Table.Settings.use())
          ? initial?.toggle ?? "table"
          : initial?.toggle ?? "calendar",
      });

      const when = function <A>({
        calendar,
        table,
      }: {
        readonly calendar: () => A;
        readonly table: () => A;
      }): A {
        return toggle === "calendar" ? calendar() : table();
      };

      const update = (to?: "calendar" | "table") => {
        const updated = Time.DateTime.now();
        return to
          ? updateToggle({ toggle: to, updated })
          : when({
              calendar: () => updateToggle({ toggle: "table", updated }),
              table: () => updateToggle({ toggle: "calendar", updated }),
            });
      };

      return { toggle: { when, update, updated } };
    };
  }

  export const Header = ({
    missingDVIRs,
    ...props
  }: PropsWithData & Toggle.Props) => {
    const Interval = () =>
      React.useMemo(() => <Summaries.Interval {...props} />, [
        props.interval.start,
        props.interval.stop,
      ]);

    return (
      <Layout.Section.Header.Component
      Left={() =>
        missingDVIRs ? (
          <Typography.H2.Component>
            Logs without DVIRs
          </Typography.H2.Component>
        ) : (
          <Controls {...props} />
        )
      }
        Right={() =>
          props.driver ? (
            <ExportMenu {...props} />
          ) : (
            <Download missingDVIRs={missingDVIRs} {...props} />
          )
        }
      >
        <Interval />
      </Layout.Section.Header.Component>
    );
  };

  const Controls = ({ toggle }: Toggle.Props) => {
    return (
      <Radio.Group
        onChange={() => toggle.update()}
        value={toggle.when({
          calendar: () => "calendar",
          table: () => "table",
        })}
      >
        <Radio.Button value="calendar">
          <Layout.Space>
            <Icon.Calendar />
            Calendar
          </Layout.Space>
        </Radio.Button>
        <Radio.Button value="table">
          <Layout.Space>
            <Icon.Table />
            Table
          </Layout.Space>
        </Radio.Button>
      </Radio.Group>
    );
  };
}

export type Summary = {
  readonly summary: Time.DateTime &
    Time.Duration &
    Distance &
    Summary.Issues & {
      readonly driver: Driver.Driver;
      readonly exemption?: Data.Reference.Reference;
      readonly shift?: Driver.Shift & {
        readonly dvirSummaries?: readonly DVIRs.Summary.DriverDvirSummary[];
        readonly distanceByAsset?: Readonly<Record<string, Distance.Miles>>;
      };
    };
};

export namespace Summary {
  export const DateTime = ({ summary }: Summary) => (
    <Time.DateTime {...summary} Separator={() => null} Time={() => null} />
  );

  // Distance shown for a log summary. Prefer the shift's (tracker-derived)
  // drive distance when it is present and non-zero, otherwise fall back to the
  // batch-computed daily summary distance. The batch value can be stale or zero
  // (e.g. shifts that straddle midnight), so the table, CSV and PDF exports all
  // route through this single selection to stay consistent.
  export const distanceMiles = (
    summary: Summary["summary"]
  ): Distance.Miles =>
    summary.shift?.distance && summary.shift.distance.miles > 0
      ? summary.shift.distance
      : summary.distance;

  export const Duration = ({
    NotApplicable = GenericNotApplicable,
    summary,
  }: NotApplicable & { readonly summary: Time.Duration }) =>
    summary.duration <= 0 ? <NotApplicable /> : <Time.Duration {...summary} />;

  export const Exemption = ({
    NotApplicable = GenericNotApplicable,
    summary: { exemption },
  }: NotApplicable & Summary) =>
    !exemption ? <NotApplicable /> : <>Exemption {exemption}</>;

  export namespace Response {
    export const runtime = () =>
      Runtime.intersection([
        Driver.Name.Response.runtime(),
        Distance.Response.runtime(),
        Issues.Response.runtime(),
        Runtime.strict({
          date: Time.DateTime.Response.runtime(),
          driver_id: Runtime.number(),
          on_duty: Time.Duration.Response.runtime(),
          exception_type_id: Runtime.nullable(Runtime.number()),
        }),
      ]);

    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Summary => ({
      summary: {
        ...response.date,
        ...response.on_duty,
        ...Distance.Response.decode(response),
        ...Issues.Response.decode(response),
        driver: {
          id: `${response.driver_id}`,
          name: Driver.Name.Response.decode(response),
        },
        exemption: response.exception_type_id
          ? { id: `${response.exception_type_id}` }
          : undefined,
      },
    });
  }

  export type Issues = {
    readonly issues: {
      readonly notSigned: boolean;
      readonly missingDVIR?: boolean;
      readonly violations: {
        readonly break: boolean;
        readonly cycle: boolean;
        readonly drive: boolean;
        readonly shift: boolean;
      };
    };
  };

  export function Issues({
    NotApplicable = GenericNotApplicable,
    issues: { notSigned, violations, missingDVIR },
  }: NotApplicable & Issues) {
    return pipe(
      [
        [
          Issues.violationBreak(),
          violations.break,
          <Icon.TimesCircle />,
          "error",
        ],
        [
          Issues.violationCycle(),
          violations.cycle,
          <Icon.TimesCircle />,
          "error",
        ],
        [
          Issues.violationDrive(),
          violations.drive,
          <Icon.TimesCircle />,
          "error",
        ],
        [
          Issues.violationShift(),
          violations.shift,
          <Icon.TimesCircle />,
          "error",
        ],
        [Issues.notSigned(), notSigned, <Icon.ExclamationCircle />, "warning"],
        [
          Issues.missingDVIR(),
          missingDVIR,
          <Icon.ExclamationCircle />,
          "warning",
        ],
      ] as const,
      ReadonlyArray.filterMap(([issue, exists, icon, level]) =>
        pipe(
          !exists
            ? Option.none
            : Option.some(
                <Tag key={issue} color={level} icon={icon}>
                  <Typography.Component strong>{issue}</Typography.Component>
                </Tag>
              )
        )
      ),
      NonEmptyArray.fromReadonlyArray,
      Option.map((issues) => <>{issues}</>),
      Option.getOrElse(() => <NotApplicable />)
    );
  }

  export type Issue = ReturnType<typeof Issues.list>[number];
  export namespace Issues {
    export const violationBreak = () => "Break Violation" as const;
    export const violationCycle = () => "Cycle Violation" as const;
    export const violationDrive = () => "Drive Violation" as const;
    export const violationShift = () => "Shift Violation" as const;
    export const notSigned = () => "Not Signed" as const;
    export const missingDVIR = () => "Missing DVIR" as const;
    export const list = () =>
      [
        violationBreak(),
        violationCycle(),
        violationDrive(),
        violationShift(),
        notSigned(),
        missingDVIR(),
      ] as const;

    export const compare: Order.Compare<Issues> = (a, b) =>
      Order.number().compare(toNumber(a), toNumber(b));

    const toNumber = ({
      issues: { notSigned, violations, missingDVIR },
    }: Issues): number =>
      (notSigned ? 1 : 0) +
      (missingDVIR ? 1 : 0) +
      2 *
        ((violations.break ? 1 : 0) +
          (violations.cycle ? 1 : 0) +
          (violations.drive ? 1 : 0) +
          (violations.shift ? 1 : 0));

    export namespace Response {
      export const runtime = () =>
        Runtime.strict({
          signed: Runtime.boolean(),
          violation_break: Runtime.boolean(),
          violation_cycle: Runtime.boolean(),
          violation_drive: Runtime.boolean(),
          violation_shift: Runtime.boolean(),
        });

      export const decode = (
        response: Runtime.ToType<typeof runtime>
      ): Issues => {
        return {
          issues: {
            notSigned: !response.signed,
            violations: {
              break: response.violation_break,
              cycle: response.violation_cycle,
              drive: response.violation_drive,
              shift: response.violation_shift,
            },
          },
        };
      };
    }

    export type Total = {
      readonly total: {
        readonly issues: {
          readonly notSigned: number;
          readonly missingDVIR: number;
          readonly violations: {
            readonly break: number;
            readonly cycle: number;
            readonly drive: number;
            readonly shift: number;
          };
        };
      };
    };

    const pluralizeIfNeeded = (count: number, issue: Issue) =>
      count > 1 && issue !== notSigned() ? `${issue}s` : issue;

    export function Total({
      total: { issues },
      onClick,
    }: Total & { readonly onClick?: (issue: Issue) => void }) {
      return pipe(
        [
          [violationBreak(), issues.violations.break, "error"],
          [violationCycle(), issues.violations.cycle, "error"],
          [violationDrive(), issues.violations.drive, "error"],
          [violationShift(), issues.violations.drive, "error"],
          [notSigned(), issues.notSigned, "warning"],
          [missingDVIR(), issues.missingDVIR, "default"],
        ] as const,
        ReadonlyArray.map(([issue, total, level]) =>
          pipe(
            total,
            Option.fromPredicate((total) => total > 0),
            Option.map((total) => (
              <Tag
                key={issue}
                onClick={(event) => {
                  // eslint-disable-next-line functional/no-expression-statement
                  event.stopPropagation();
                  return onClick?.(issue);
                }}
                color={level}
                icon={<Total.Number>{total}</Total.Number>}
                style={{ cursor: "pointer" }}
              >
                <Typography.Component>
                  {pluralizeIfNeeded(total, issue)}
                </Typography.Component>
              </Tag>
            )),
            Option.toNullable
          )
        ),
        (issues) => <>{issues}</>
      );
    }

    export namespace Total {
      export const Number = ({
        children,
      }: {
        readonly children?: React.ReactNode;
      }) => (
        <Layout.Inline.Component right>
          <Typography.Component>{children}</Typography.Component>
        </Layout.Inline.Component>
      );

      export const isEmpty = ({
        total: {
          issues: { notSigned, violations, missingDVIR },
        },
      }: Total): boolean =>
        notSigned +
          missingDVIR +
          violations.break +
          violations.cycle +
          violations.drive +
          violations.shift <=
        0;

      export const empty = (): Total => ({
        total: {
          issues: {
            notSigned: 0,
            missingDVIR: 0,
            violations: { break: 0, cycle: 0, drive: 0, shift: 0 },
          },
        },
      });

      export const concat = (a: Total, b: Total): Total => ({
        total: {
          issues: {
            notSigned: a.total.issues.notSigned + b.total.issues.notSigned,
            missingDVIR:
              a.total.issues.missingDVIR + b.total.issues.missingDVIR,
            violations: {
              break:
                a.total.issues.violations.break +
                b.total.issues.violations.break,
              cycle:
                a.total.issues.violations.cycle +
                b.total.issues.violations.cycle,
              drive:
                a.total.issues.violations.drive +
                b.total.issues.violations.drive,
              shift:
                a.total.issues.violations.break +
                b.total.issues.violations.shift,
            },
          },
        },
      });

      export const fromIssues = ({ issues }: Issues): Total => ({
        total: {
          issues: {
            notSigned: issues.notSigned ? 1 : 0,
            missingDVIR: issues.missingDVIR ? 1 : 0,
            violations: {
              break: issues.violations.break ? 1 : 0,
              cycle: issues.violations.cycle ? 1 : 0,
              drive: issues.violations.drive ? 1 : 0,
              shift: issues.violations.shift ? 1 : 0,
            },
          },
        },
      });
    }
  }
}
